import { GridWrapper, h4, mediaQueries } from '@theme/lib';
import React, { ReactElement, useState } from 'react';
import styled from 'styled-components';

import { AccordionParagraph, WYSIWYGFragment } from '@/__generated__/graphql';
import { getLanguage } from '@/lib/config';
import WYSIWYG from '../WYSIWYG';


const AccordionWrapper = styled(GridWrapper)`
  .accordion-item {
    border-radius: ${props => props.theme.config.accordionRadius};
    margin-top: 0.5rem;

    &--closed {
      .accordion-label {
        &::after {
          -webkit-mask-image: url(/Accordion/arrow-down.svg);
        }
        &:hover {
          text-decoration: none;
          color: ${(props) => props.theme.colors.accordionIconColour};
          border-color: ${(props) => props.theme.colors.accordionTabFocusColour};
        }
      }

      .accordion-content {
        display: none;
      }
    }

    &--opened {
      .accordion-label {
        border-radius: ${props => `${props.theme.config.accordionRadius} ${props.theme.config.accordionRadius} 0 0`};
        border-color: ${(props) => props.theme.colors.accordionTabBorderColour};
        border-width: 1px 1px 0 1px;
        border-style: solid;
        background-color: transparent;

        &:hover, &:focus {
          color: ${(props) => props.theme.colors.link};
        }

        &::after {
          -webkit-mask-image: url(/Accordion/arrow-up.svg);
        }
      }
    }
  }

  .accordion-label {
    ${h4};
    background: ${(props) => props.theme.colors.accordionTabBgr};
    border-radius: ${props => props.theme.config.accordionRadius};
    border: ${(props) => props.theme.key === 'zsl' ? '0px' : '3px'} solid transparent;
    color: ${(props) => props.theme.colors.accordionTabColour};
    display: block;
    padding: 1rem 2.75rem 1rem 1rem;
    position: relative;

    &:hover, &:focus {
      text-decoration: none;
    }

    &::after {
      background-color: ${(props) => props.theme.colors.accordionIconColour};
      content: "";
      width: 16px;
      height: 9px;
      position: absolute;
      top: calc(50% - 4.5px);
      right: 1.53125rem;
    }
  }

  .accordion-content {
    color: ${(props) => props.theme.colors.accordionTabColour};
    border-radius: ${props => `0 0 ${props.theme.config.accordionRadius} ${props.theme.config.accordionRadius}`};
    border-color: ${(props) => props.theme.colors.accordionTabBorderColour};
    border-width: 0 1px 1px 1px;
    border-style: solid;
    padding: 0 1.5rem 1.5rem 1rem;

    ul, ol {
      list-style-position: outside;
      padding-left: 1rem;
      li:first-child > :first-child {
        margin-top: 0;
      }
    }
  }

  ${mediaQueries.md} {
    .accordion-label {
      font-size: 1.5rem;
      line-height: 1.8rem;
      padding: 1rem 3.5rem 1rem 1.5rem;

      &::after {
        right: 1.6875rem;
      }
    }

    .accordion-content {
      padding-left: 1.5rem;
      padding-right: 1.5rem;
    }
  }
`;

interface AccordionTabProps {
  title: string;
  content: {
    processed: string;
    format: string,
    value: string,
  }
  state?: {
    opened: boolean;
  }
}

interface AccordionProps {
  alignment: string;
  tabs: Array<AccordionTabProps>;
}

export default function Accordion({alignment, tabs}: AccordionParagraph): ReactElement {
  const [openedStates, setOpenedStates] = useState<number[]>([]);
  const language = getLanguage();

  const toggleTabState = (e: React.MouseEvent<HTMLElement>, tabNum: number) => {
    e.preventDefault();

    let states = [...openedStates];
    if (!openedStates.includes(tabNum)) {
      states.push(tabNum);
    } else {
      states = openedStates.filter((_tabNum) => _tabNum !== tabNum);
    }

    setOpenedStates(states);
  };

  return <AccordionWrapper alignment={alignment}>
    {tabs.map((tab, key) =>
      <div key={key} className={`accordion-item ${openedStates.includes(key) ? 'accordion-item--opened' : 'accordion-item--closed'}`}>
        <a className="accordion-label" href={`#item-${key+1}`} onClick={e => toggleTabState(e, key)} lang={language !== 'en' ? language : undefined}>{tab.title}</a>
        <div className="accordion-content">
          <WYSIWYG id={`accordion-${key}`} text={tab.content as WYSIWYGFragment['text']} alignment="left" />
        </div>
      </div>
    )}
  </AccordionWrapper>
}
